import { Button, Stack, Text } from '@chakra-ui/react'
import type { DocumentClass, DocumentNameBody } from '@ipc/shared'
import { ActionBar } from '~/components/member/ActionBar'
import { Rich, useT } from '~/i18n/use-t'

/**
 * "We read your name as: <name> Is this you?" (spec §12.1 step 4, A20). The name is the first name the server kept
 * from the document; it can be in Hebrew or Arabic, so it sits in a `<bdi dir="auto">` and cannot reorder the words
 * around it. [Yes] and [Not right] both go on to the next step: "Not right" only tells a person to look at the name.
 */
export function NameCheck({
  name,
  pending,
  disputed,
  recognised = null,
  onAnswer,
}: {
  name: string
  /** The answer being sent, if any (its button shows the spinner, both buttons wait). */
  pending: DocumentNameBody['answer'] | null
  /** The member said "Not right" and the answer was saved. */
  disputed: boolean
  /** The class the provider read when it is not the one the member chose (face-first spec §5.2, §6). */
  recognised?: DocumentClass | null
  onAnswer: (answer: DocumentNameBody['answer']) => void
}) {
  const { t } = useT()
  return (
    <Stack gap={6}>
      <Text fontSize='lg'>
        <Rich k='join.document.nameQuestion' values={{ name }} components={{ name: <bdi dir='auto' /> }} />
      </Text>
      {recognised && (
        <Text role='note' color='fg.muted'>
          {t('join.document.rewritten', { class: t(`join.document.class.${recognised}`).toLocaleLowerCase('en') })}
        </Text>
      )}
      {disputed && (
        <Text role='status' color='fg.muted'>
          {t('join.document.notRightThanks')}
        </Text>
      )}
      <ActionBar gap={3}>
        <Button
          size='lg'
          w='full'
          loading={pending === 'confirmed'}
          disabled={pending !== null}
          onClick={() => onAnswer('confirmed')}
        >
          {t('join.document.yes')}
        </Button>
        <Button
          size='lg'
          w='full'
          variant='outline'
          loading={pending === 'disputed'}
          disabled={pending !== null}
          onClick={() => onAnswer('disputed')}
        >
          {t('join.document.notRight')}
        </Button>
      </ActionBar>
    </Stack>
  )
}
